import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { extractErrorMessage } from '../utils/apiFetch';

const validationSchema = yup.object({
  email: yup.string().email('Ungültige E-Mail').required('E-Mail ist erforderlich'),
  password: yup.string().required('Passwort ist erforderlich'),
});

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const { login, twoFactorChallenge, verifyTwoFactor, verifyBackupCode, cancelTwoFactor } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);

      try {
        await login(values);
        // If 2FA is required, twoFactorChallenge will be set by AuthContext
        // and we show the 2FA form instead of navigating
        if (!twoFactorChallenge) {
          navigate('/dashboard');
        }
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Login fehlgeschlagen. Bitte versuche es erneut.'));
      } finally {
        setLoading(false);
      }
    },
  });

  const handleVerify2FA = async () => {
    setLoading(true);
    setError(null);
    try {
      if (useBackupCode) {
        await verifyBackupCode(backupCode);
      } else {
        await verifyTwoFactor(totpCode);
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Verifizierung fehlgeschlagen'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    cancelTwoFactor();
    setTotpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>Login - btrader</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              {twoFactorChallenge ? '2FA Verifizierung' : 'Anmelden'}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              {twoFactorChallenge
                ? useBackupCode
                  ? 'Gib einen deiner Backup-Codes ein. Jeder Code kann nur einmal verwendet werden.'
                  : 'Gib den 6-stelligen Code aus deiner Authenticator-App ein.'
                : 'Willkommen zurück bei btrader'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {twoFactorChallenge ? (
              // 2FA verification form
              <Box>
                {!useBackupCode ? (
                  <TextField
                    fullWidth
                    id="totp"
                    label="2FA-Code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    margin="normal"
                    autoFocus
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    placeholder="000000"
                  />
                ) : (
                  <TextField
                    fullWidth
                    id="backup-code"
                    label="Backup-Code"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                    margin="normal"
                    autoFocus
                    inputProps={{ maxLength: 10 }}
                    placeholder="ABC123"
                  />
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setUseBackupCode(!useBackupCode);
                    setTotpCode('');
                    setBackupCode('');
                    setError(null);
                  }}
                  sx={{ mb: 1 }}
                >
                  {useBackupCode ? 'Authenticator-Code verwenden' : 'Backup-Code verwenden'}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  onClick={handleVerify2FA}
                  disabled={loading || (useBackupCode ? backupCode.length < 4 : totpCode.length < 6)}
                  sx={{ mt: 2, mb: 1, height: 48 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verifizieren'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleCancel2FA}
                  sx={{ mb: 2 }}
                >
                  Abbrechen
                </Button>
              </Box>
            ) : (
              // Login form
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="E-Mail"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  margin="normal"
                  autoComplete="email"
                />
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Passwort"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  margin="normal"
                  autoComplete="current-password"
                />
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, height: 48 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Anmelden'}
                </Button>
              </form>
            )}

            <Box textAlign="center">
              <Link component={RouterLink} to="/register" variant="body2">
                Noch kein Konto? Registrieren
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default LoginPage;